import { DragDndItemsType, MenuDndItemsType } from "@/common/type/type";
import { useEffect, useRef, useState } from "react";
import { DropResult } from "@hello-pangea/dnd";
import {
  DataViewType,
  TrendViewType,
  WidgetDataType,
  WidgetType,
} from "@/pages/dashboard/type";
import { handleDelete } from "@/helpers/widgetDelete";
import { useLoading } from "./useLoading";

interface UseDragAndDropProps {
  initialMenuItems: MenuDndItemsType;
  machineIds: number[];
  setDataWidgets: React.Dispatch<React.SetStateAction<DataViewType[]>>;
  setTrendWidgets: React.Dispatch<React.SetStateAction<TrendViewType[]>>;
  setWidgetQueue: React.Dispatch<React.SetStateAction<WidgetType[]>>;
  setTotalWidgets: React.Dispatch<React.SetStateAction<WidgetDataType[]>>;
}

export const useDragAndDrop = ({
  initialMenuItems,
  machineIds,
  setDataWidgets,
  setTrendWidgets,
  setWidgetQueue,
  setTotalWidgets,
}: UseDragAndDropProps) => {
  const [menuItems] = useState(initialMenuItems);
  const [widgetId, setWidgetId] = useState<number>(0);
  const [nextId, setNextId] = useState(0);
  const machinePositions = useRef<{ [key: number]: number }>({});
  const machineSortIds = useRef<{ [key: number]: number }>({});
  const [dragAreaItems, setDragAreaItems] = useState<DragDndItemsType[]>(
    Array(5).fill([])
  );
  const { loading, startLoading, stopLoading } = useLoading();
  const [widgetIdCounter, setWidgetIdCounter] = useState<
    Record<number, number>
  >({
    1: 0,
    2: 0,
  });

  useEffect(() => {
    if (!machineIds || machineIds.length === 0) return;

    const fetchDashboardData = async () => {
      startLoading();
      try {
        const queryString = machineIds.join(",");
        const response = await fetch(
          `/api/dashboard?machineIds=${queryString}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const data = await response.json();
        if (response.ok) {
          let maxWidgetId = 0;

          const updatedDragAreaItems = dragAreaItems.map((items) => [...items]);
          const newMachinePositionMap: { [key: number]: number } = {};

          data.result.forEach(({ machineId, widgets }: any) => {
            const index = machineIds.indexOf(machineId);

            if (!newMachinePositionMap[machineId]) {
              newMachinePositionMap[machineId] = 0;
            }

            widgets.forEach((widget: any) => {
              const widgetId = widget.widgetId;
              const type = widget.widgetType;
              const position = widget.position;
              const sortId = newMachinePositionMap[machineId];

              updatedDragAreaItems[index].push({
                id: maxWidgetId++,
                widgetId,
                type,
                position,
                sortId,
              });

              widgetIdCounter[type] = widgetId;
              newMachinePositionMap[machineId]++;
            });
          });

          updatedDragAreaItems.forEach((items, index) => {
            updatedDragAreaItems[index] = items.sort(
              (a, b) => a.position - b.position
            );
          });

          setDragAreaItems(updatedDragAreaItems);
          setNextId(maxWidgetId);
          setWidgetId(maxWidgetId);

          const widgetList: WidgetType[] = [];
          for (const machine of data.result) {
            for (const widget of machine.widgets) {
              widgetList.push({
                machineId: machine.machineId,
                widgetId: widget.widgetId,
                widgetType: widget.widgetType,
              });
            }
          }
          setWidgetQueue(widgetList);
        } else {
          console.error("サーバ接続エラー:", response.statusText);
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      } finally {
        stopLoading();
      }
    };

    fetchDashboardData();
  }, []);

  const getNextId = () => {
    setNextId((prev) => prev + 1);
    return nextId;
  };

  const getNextWidgetId = (type: number) => {
    setWidgetIdCounter((prev) => {
      const newCounter = { ...prev };
      newCounter[type] += 1;
      return newCounter;
    });
    return widgetIdCounter[type] + 1;
  };

  const copyItem = (
    sourceItems: MenuDndItemsType,
    destinationItems: DragDndItemsType,
    sourceIdx: number,
    destinationIdx: number,
    _: number,
    machineId: number
  ) => {
    const itemToCopy = sourceItems[sourceIdx];
    if (!itemToCopy || !setTotalWidgets) return;

    const { content, ...newItem } = itemToCopy;

    if (!machinePositions.current[machineId]) {
      machinePositions.current[machineId] = 0;
    }
    if (!machineSortIds.current[machineId]) {
      machineSortIds.current[machineId] = 0;
    }

    const lastPosition = destinationItems.length
      ? Math.max(...destinationItems.map((item) => item.position), 0) + 1
      : 0;
    const newPosition =
      destinationIdx === destinationItems.length
        ? lastPosition
        : destinationIdx + 1;

    const newSortId =
      Math.max(...destinationItems.map((item) => item.sortId), -1) + 1;

    const copiedItem = {
      ...newItem,
      id: getNextId(),
      widgetId: getNextWidgetId(itemToCopy.type === "data" ? 1 : 2),
      type: itemToCopy.type === "data" ? 1 : 2,
      position: newPosition,
      sortId: newSortId,
    };

    const updatedItems = destinationItems.map((item, index) => {
      if (index >= destinationIdx) {
        return {
          ...item,
          position: item.position + 1,
        };
      }
      return item;
    });

    const finalItems = [...updatedItems, copiedItem].sort(
      (a, b) => a.position - b.position
    );

    destinationItems.splice(0, destinationItems.length, ...finalItems);

    setTotalWidgets((prevWidgets) => {
      const updatedWidgets = prevWidgets.map((widget) => {
        if (widget.machineId === machineId) {
          const updatedDataWidgets = destinationItems
            .filter((item) => item.type === 1)
            .map((item) => {
              const existingWidget = widget.dataWidgets.find(
                (w) => w.sortId === item.sortId
              );

              return existingWidget
                ? { ...existingWidget, position: item.position }
                : {
                    dataNames: [""],
                    position: item.position,
                    sortId: item.sortId,
                  };
            });

          const updatedTrendWidgets = destinationItems
            .filter((item) => item.type === 2)
            .map((item) => {
              const existingWidget = widget.trendWidgets.find(
                (w) => w.sortId === item.sortId
              );

              return existingWidget
                ? { ...existingWidget, position: item.position }
                : {
                    dataName1: null,
                    dataName2: null,
                    dataName3: null,
                    dataName4: null,
                    dataName5: null,
                    position: item.position,
                    sortId: item.sortId,
                  };
            });

          return {
            ...widget,
            dataWidgets:
              updatedDataWidgets.length > 0
                ? updatedDataWidgets
                : widget.dataWidgets,
            trendWidgets:
              updatedTrendWidgets.length > 0
                ? updatedTrendWidgets
                : widget.trendWidgets,
          };
        }
        return widget;
      });

      return updatedWidgets;
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceIndex = parseInt(
      source.droppableId.replace("dragArea", ""),
      10
    );
    const destinationIndex = parseInt(
      destination.droppableId.replace("dragArea", ""),
      10
    );

    const sourIndex = machineIds.indexOf(sourceIndex);
    const destIndex = machineIds.indexOf(destinationIndex);
    const updatedDragAreaItems: DragDndItemsType[] = dragAreaItems.map(
      (items) => [...items]
    );

    if (
      source.droppableId === "menu" &&
      destination.droppableId.startsWith("dragArea")
    ) {
      const machineId = destinationIndex;
      copyItem(
        menuItems,
        updatedDragAreaItems[destIndex],
        source.index,
        destination.index,
        menuItems[source.index].type === "data" ? 1 : 2,
        machineId
      );
      setWidgetId((prev) => prev + 1);
    } else if (
      source.droppableId.startsWith("dragArea") &&
      destination.droppableId === "menu"
    ) {
      handleDelete(
        updatedDragAreaItems[sourIndex][source.index].id,
        sourceIndex + 1,
        updatedDragAreaItems[sourIndex],
        setTotalWidgets as React.Dispatch<
          React.SetStateAction<WidgetDataType[]>
        >
      );
    } else if (
      source.droppableId.startsWith("dragArea") &&
      destination.droppableId.startsWith("dragArea")
    ) {
      const sourceItems = updatedDragAreaItems[sourIndex];
      const [movedItem] = sourceItems.splice(source.index, 1);
      if (movedItem) {
        updatedDragAreaItems[destIndex].splice(destination.index, 0, movedItem);

        updatedDragAreaItems[destIndex] = updatedDragAreaItems[destIndex].map(
          (item, index) => ({
            ...item,
            position: index + 1,
          })
        );

        setTotalWidgets((prevWidgets) =>
          prevWidgets.map((widget) => {
            if (widget.machineId === destinationIndex) {
              const updatedDataWidgets = widget.dataWidgets.map((w) => {
                const foundItem = updatedDragAreaItems[destIndex].find(
                  (item) => item.sortId === w.sortId
                );

                const updatedWidget = {
                  ...w,
                  position: foundItem ? foundItem.position : w.position,
                };

                setDataWidgets((prevDataWidgets) => {
                  return prevDataWidgets.map((dataWidget) =>
                    dataWidget.sortId === w.sortId
                      ? { ...dataWidget, position: updatedWidget.position }
                      : dataWidget
                  );
                });

                return updatedWidget;
              });

              const updatedTrendWidgets = widget.trendWidgets.map((w) => {
                const foundItem = updatedDragAreaItems[destIndex].find(
                  (item) => item.sortId === w.sortId
                );

                const updatedWidget = {
                  ...w,
                  position: foundItem ? foundItem.position : w.position,
                };

                setTrendWidgets((prevTrendWidgets) => {
                  return prevTrendWidgets.map((trendWidget) =>
                    trendWidget.sortId === w.sortId
                      ? { ...trendWidget, position: updatedWidget.position }
                      : trendWidget
                  );
                });

                return updatedWidget;
              });

              return {
                ...widget,
                dataWidgets: updatedDataWidgets,
                trendWidgets: updatedTrendWidgets,
              };
            }
            return widget;
          })
        );
      }
    }

    setDragAreaItems(updatedDragAreaItems);
  };

  return {
    loading,
    menuItems,
    widgetId,
    dragAreaItems1: dragAreaItems[0],
    dragAreaItems2: dragAreaItems[1],
    dragAreaItems3: dragAreaItems[2],
    dragAreaItems4: dragAreaItems[3],
    dragAreaItems5: dragAreaItems[4],
    handleDragEnd,
  };
};
